import { formatArea } from "@nova/format"
import { InfraredFlight } from "@nova/protobuf"
import { selectWorkspaceAreaUnit } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectHotspotsSizeGraphByFlightId } from "features/entity-caching/hotspots/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import { useCallback, useEffect, useMemo, useState } from "react"
import { TwoThumbSlider } from "../../icons/TODO-put-in-package/TwoThumbSlider"
import { InfraredFlightUpdates } from "../InspectorPanelHotspotLayer"
import { InspectorPanelHotspotsLayerFiltersNotEnoughData } from "./InspectorPanelHotspotsLayerFiltersNotEnoughData"
import { findBinForValue, MAX_HEIGHT, MIN_HEIGHT, NUMBER_OF_BARS } from "./module"

export const InspectorPanelHotspotLayerFiltersSize = ({
    flight,
    updateFn,
}: {
    flight: InfraredFlight
    updateFn: (updates: InfraredFlightUpdates) => void
}) => {
    // These refer to the slider values on the graph.
    const [selectedLowerBoundBin, setSelectedLowerBoundBin] = useState(0)
    const [selectedUpperBoundBin, setSelectedUpperBoundBin] = useState(NUMBER_OF_BARS)

    const {
        numberOfHotspotsPerBin,
        // leastHotspotsPerBar,
        mostHotspotsPerBin,
        barBounds,
        absoluteGraphMinimum,
        absoluteGraphMaximum,
    } = useAppSelector(selectHotspotsSizeGraphByFlightId(flight.id))

    // After the context of the graph changes. The slider values need to be updated accordingly.
    useEffect(() => {
        const entityMin = flight.filters?.size?.min
        const entityMax = flight.filters?.size?.max

        const selectedLowerBoundBin =
            entityMin !== undefined
                ? Math.min(
                      Math.max(findBinForValue({ value: entityMin, barBounds }), 0),
                      NUMBER_OF_BARS - 1
                  )
                : 0
        const selectedUpperBoundBin =
            entityMax !== undefined
                ? Math.min(
                      Math.max(findBinForValue({ value: entityMax, barBounds }), 1),
                      NUMBER_OF_BARS
                  )
                : NUMBER_OF_BARS

        setSelectedLowerBoundBin(selectedLowerBoundBin)
        setSelectedUpperBoundBin(selectedUpperBoundBin)
    }, [
        barBounds,
        absoluteGraphMinimum,
        absoluteGraphMaximum,
        flight.filters?.size?.min,
        flight.filters?.size?.max,
    ])

    const updateSelectedRange = useCallback(
        ({ lowerBoundBin, upperBoundBin }: { lowerBoundBin?: number; upperBoundBin?: number }) => {
            if (!flight) return

            const updates = {
                filters: {
                    ...flight.filters,
                    size: {
                        min:
                            lowerBoundBin !== undefined
                                ? lowerBoundBin !== 0
                                    ? barBounds[lowerBoundBin]
                                    : undefined
                                : flight?.filters?.size?.min,
                        max:
                            upperBoundBin !== undefined
                                ? upperBoundBin !== NUMBER_OF_BARS
                                    ? barBounds[upperBoundBin]
                                    : undefined
                                : flight?.filters?.size?.max,
                    },
                },
            }

            updateFn(updates)

            if (lowerBoundBin !== undefined) setSelectedLowerBoundBin(lowerBoundBin)
            if (upperBoundBin !== undefined) setSelectedUpperBoundBin(upperBoundBin)
        },
        [flight, barBounds, updateFn]
    )

    const areaUnit = useAppSelector(selectWorkspaceAreaUnit)
    const { formattedMinSize, formattedMaxSize } = useMemo(() => {
        return {
            formattedMinSize: formatArea({
                value:
                    selectedLowerBoundBin === 0
                        ? absoluteGraphMinimum
                        : barBounds[selectedLowerBoundBin],
                unit: areaUnit,
                waterfall: true,
            }),
            formattedMaxSize: formatArea({
                value:
                    selectedUpperBoundBin === NUMBER_OF_BARS
                        ? absoluteGraphMaximum
                        : barBounds[selectedUpperBoundBin],
                unit: areaUnit,
                waterfall: true,
            }),
        }
    }, [
        barBounds,
        selectedLowerBoundBin,
        selectedUpperBoundBin,
        absoluteGraphMinimum,
        absoluteGraphMaximum,
        areaUnit,
    ])

    const calculateBarHeight = useCallback(
        (i: number) => {
            const numberOfHotspots = numberOfHotspotsPerBin[i]
            const percentage = mostHotspotsPerBin > 0 ? numberOfHotspots / mostHotspotsPerBin : 0
            return percentage * (MAX_HEIGHT - MIN_HEIGHT)
        },
        [numberOfHotspotsPerBin, mostHotspotsPerBin]
    )

    const color = (i: number) => {
        if (i >= selectedLowerBoundBin && i < selectedUpperBoundBin) return "#8D3620" // red-600
        return "#FFFFFF1A" // white-200
    }

    return (
        <div className="flex flex-col gap-2">
            {/* Title */}
            <h5 className="text-gray-0 select-none">Hotspot Size</h5>

            {absoluteGraphMaximum === 0 || absoluteGraphMaximum === absoluteGraphMinimum ? (
                <InspectorPanelHotspotsLayerFiltersNotEnoughData />
            ) : (
                <>
                    {/* Graph */}
                    <div className="relative mx-[0.4375rem]">
                        <svg className="mx-[0.4375rem]" viewBox={`0 0 190 ${MAX_HEIGHT}`}>
                            {Array(NUMBER_OF_BARS)
                                .fill(null)
                                .map((_, i) => (
                                    <path
                                        key={i}
                                        d={`
                    M ${i * 6} ${MAX_HEIGHT - calculateBarHeight(i)}
                    a 2 2 0 0 1 4 0
                    v ${calculateBarHeight(i)}
                    h -4
                    z
                `}
                                        fill={color(i)}
                                    />
                                ))}
                        </svg>

                        <TwoThumbSlider
                            min={0}
                            max={NUMBER_OF_BARS}
                            selectedMin={selectedLowerBoundBin}
                            selectedMax={selectedUpperBoundBin}
                            setSelectedMin={(lowerBoundBin) =>
                                updateSelectedRange({ lowerBoundBin })
                            }
                            setSelectedMax={(upperBoundBin) =>
                                updateSelectedRange({ upperBoundBin })
                            }
                            isDisabled={barBounds.at(-1) === 0}
                        />
                    </div>

                    <div className="flex items-center justify-between text-gray-100 mx-[0.4375rem] select-none">
                        <small className="mono">{formattedMinSize}</small>
                        <small className="mono">{formattedMaxSize}</small>
                    </div>
                </>
            )}
        </div>
    )
}
